/**
 * #564 — one implementation of "hand the user a file the client already has".
 *
 * The synthesize-an-anchor idiom was copy-pasted into six surfaces (CirclePage,
 * HealthPage, UserRoster, PollDetailPage, WilliamsPage,
 * FamilyIntakeDrawer) in three slightly different shapes, and every copy
 * revoked the object URL on the line straight after `.click()`.
 *
 * That synchronous revoke is a race, not a tidy-up: `.click()` only *queues*
 * the browser's download, which reads the blob URL after the handler returns.
 * Revoking first is a coin flip between a working save and a failed or empty
 * one, and the odds get worse as the blob gets bigger. The precedent for the
 * fix is already in this codebase — `AdminFeedbackPage.tsx` defers its revoke
 * by a minute, and `documents.ts` generalized that into a module-level TTL
 * registry (#556) after a component-lifecycle-scoped version revoked URLs a
 * just-opened tab was still reading.
 *
 * So this helper reuses that registry rather than starting a second, competing
 * revoke policy: `trackBlobUrl` gives every URL the shared 10-minute TTL plus
 * the `pagehide` sweep, which bounds the leak without racing the download.
 */

import { trackBlobUrl } from './documents';

/**
 * Save `blob` to the user's machine as `filename`.
 *
 * Must be called from a user-gesture handler (a click), like any programmatic
 * download — browsers block anchor clicks synthesized outside one.
 *
 * @param blob     the file contents
 * @param filename the name the browser should save it under, extension included
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  // Registered BEFORE the click so a throw from the click path still leaves
  // the URL scheduled for revocation.
  trackBlobUrl(url);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  // Firefox ignores a click on an anchor that is not in the document.
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
  }
}
